import { useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { LoadingButton } from '@atlaskit/button';
import { FormFooter } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Flex, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { experiments } from '@/utils/experiment/manifest.ts';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure.ts';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag.ts';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest.ts';

import { PageBoardColumnsLeftProps } from '../../../common/types.tsx';
import { useIsJFindInviteStepExperiment } from '../../../hooks/experiment-hooks.ts';

import messages from './messages.tsx';
import { ColumnInputs, onClickFinishButton } from './utils.tsx';

export const LeftHandSidePageBoardColumns = ({
	onClickNext,
	columnNames,
	setColumnNames,
	isBoardColumnsSubmitting,
}: PageBoardColumnsLeftProps) => {
	const [emptyInputIndexes, setEmptyInputIndexes] = useState<number[]>([]);
	const [dupeIndexes, setDupeIndexes] = useState<number[]>([]);
	const [exceededInputLengthIndexes, setExceededInputLengthIndexes] = useState<number[]>([]);
	const isJfind1663ExperimentEnabled = useIsJFindInviteStepExperiment();

	// FIRE EXPOSURE FOR JFIND-1663 - first visible UX change is the CTA content on this page
	useExperimentExposure(experiments.jfindOnboardingWizardInviteStep);

	const onClickNextWrapper = ({ columnNames }: { columnNames: string[] }) => {
		onClickNext({ columnNames });
	};

	const visualFeedback = checkFeatureFlagGate(featureFlags.signupVisualAlignmentFeedback);

	const buttonMsg = isJfind1663ExperimentEnabled ? (
		<FormattedMessage {...messages.nextButtonCta} />
	) : (
		<FormattedMessage {...messages.finishButtonCta} />
	);

	return (
		<>
			<Stack space={visualFeedback ? 'space.250' : 'space.200'}>
				<Stack space={visualFeedback ? 'space.100' : 'space.200'}>
					<div css={[titleCss, visualFeedback && titleVisualFeedbackCss]}>
						<Heading size="xlarge" as="h1">
							<FormattedMessage {...messages.title} />
						</Heading>
					</div>
					<p css={subtextCss}>
						<FormattedMessage {...messages.subtitle} />
					</p>
				</Stack>
				<ColumnInputs
					columnNames={columnNames}
					setColumnNames={setColumnNames}
					emptyInputIndexes={emptyInputIndexes}
					setEmptyInputIndexes={setEmptyInputIndexes}
					dupeIndexes={dupeIndexes}
					setDupeIndexes={setDupeIndexes}
					exceededInputLengthIndexes={exceededInputLengthIndexes}
					setExceededInputLengthIndexes={setExceededInputLengthIndexes}
				/>
			</Stack>
			<FormFooter>
				<Flex justifyContent="end" alignItems="center" xcss={footerStyles}>
					<div css={[submitButtonCss, visualFeedback && submitButtonVisualFeedbackCss]}>
						<LoadingButton
							appearance="primary"
							onClick={() => {
								onClickFinishButton({
									onClickNext: onClickNextWrapper,
									columnNames,
									setEmptyInputIndexes,
									setDupeIndexes,
									setExceededInputLengthIndexes,
								});
							}}
							isLoading={isBoardColumnsSubmitting}
							isDisabled={isBoardColumnsSubmitting}
						>
							{buttonMsg}
						</LoadingButton>
					</div>
				</Flex>
			</FormFooter>
		</>
	);
};

const footerStyles = xcss({
	width: '100%',
	color: 'color.text.subtlest',
});

const titleCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h1: {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 700,
		fontSize: '32px',
		lineHeight: '38px',
	},
});

const titleVisualFeedbackCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h1: {
		fontSize: '24px',
		lineHeight: '30px',
	},
});

const subtextCss = css({
	font: token('font.body'),
	color: token('color.text.subtle'),
	margin: 0,
});

const submitButtonVisualFeedbackCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		borderRadius: '6px !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		fontFamily: `var(--atlassian-sans-font-family) !important`,
	},
});

const submitButtonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		paddingBlock: '6px !important',
		paddingInline: '12px !important',
		margin: '0 !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		fontFamily: wacToken('font.charlie.text') + ' !important',
		fontSize: '14px !important',
		lineHeight: '1.5rem !important',
		fontWeight: `${token('font.weight.medium')} !important`,
		textAlign: 'center',
		margin: '0 !important',
	},
});
